import React, { useState } from 'react';
import { useAuth } from '../../lib/auth';
import { AdminTabs } from './VoiceAdmin';

/**
 * Local copy of the Parrot Design System reference. The browsable site
 * lives at `public/design-system/system/index.html` and ships its own
 * sidebar navigation, preview cards (palettes, type, spacing, components),
 * and an embedded iOS prototype tab. We iframe it here at full height.
 *
 * To swap in a new version: drop the latest `parrot-design-system/project/`
 * contents into `public/design-system/system/` (preview, fonts, assets,
 * colors_and_type.css, fonts.css, reference). The iOS bundle stays at
 * `public/design-system/parrot-prototype.html`. No React changes needed.
 */
export const DesignSystemAdmin: React.FC = () => {
  const { user, signOutUser } = useAuth();
  const [loaded, setLoaded] = useState(false);

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 flex flex-col">
      <header className="bg-white border-b border-neutral-200 shrink-0">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-lg font-bold">Design system</div>
            <div className="text-xs text-neutral-500">
              Browse tokens, components, and screens. Yellow canvas, black type, coral for moments.
            </div>
          </div>
          <div className="flex items-center gap-3">
            <a
              href="/design-system/system/index.html"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-neutral-600 underline hover:text-neutral-900"
            >
              Open in new tab ↗
            </a>
            <a
              href="/design-system/parrot-prototype.html"
              target="_blank"
              rel="noreferrer"
              className="text-xs text-neutral-600 underline hover:text-neutral-900"
            >
              iOS prototype ↗
            </a>
            <span className="text-xs text-neutral-500 font-mono">{user?.email}</span>
            <button
              onClick={signOutUser}
              className="text-xs text-neutral-600 underline hover:text-neutral-900"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl w-full mx-auto px-6 pt-6 pb-3 shrink-0">
        <AdminTabs current="design" />
      </div>

      <main className="flex-1 max-w-6xl w-full mx-auto px-6 pb-6 min-h-0">
        <div className="relative h-[calc(100vh-180px)] min-h-[600px] bg-white border border-neutral-200 rounded-xl overflow-hidden">
          {!loaded && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-neutral-500">
              <div className="text-center">
                <div className="mb-2">Loading design system…</div>
                <div className="text-xs text-neutral-400">Tokens, components, preview cards, and embedded iOS prototype.</div>
              </div>
            </div>
          )}
          <iframe
            title="Parrot Design System"
            src="/design-system/system/index.html"
            className="w-full h-full"
            style={{ border: 0, display: 'block', opacity: loaded ? 1 : 0, transition: 'opacity 0.2s' }}
            onLoad={() => setLoaded(true)}
          />
        </div>
      </main>
    </div>
  );
};
